/**
 * Parsed JSON, or `undefined` for an empty or unparseable string. Typed `unknown`: the caller
 * validates the shape, since stored and linked JSON is whatever was last written there.
 *
 * Dependency-free: the Knockout bundle compiles it too.
 */
export function parseJson(raw: string | null): unknown {
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/**
 * Deep equality of parsed JSON values, whatever the order of object keys. Here rather than
 * lodash's `isEqual` because the Knockout bundle compiles this module, and its CI build has no
 * `frontend/node_modules` to resolve a package from.
 */
export function jsonEqual(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => jsonEqual(value, right[index]))
    );
  }

  if (typeof left !== 'object' || typeof right !== 'object' || left === null || right === null) {
    return false;
  }

  const leftEntries = Object.entries(left).filter(([, value]) => value !== undefined);
  const rightValues = right as Record<string, unknown>;
  const rightKeys = Object.keys(right).filter((key) => rightValues[key] !== undefined);
  return (
    leftEntries.length === rightKeys.length &&
    leftEntries.every(([key, value]) => jsonEqual(value, rightValues[key]))
  );
}
